import { Box, Card, Image, Typography, useMediaQueryContext } from "@drone-js/core";
import { type FunctionComponent, type MouseEvent, useEffect, useState } from "react";
import { PartnerMarketingLayout } from "./PartnerMarketingLayout";
import { usePartnerMarketingModelValue } from "./partnerMarketingModelAtom";

type MediaQuery = Record<"l" | "m" | "xl", boolean>;
const CARD_TOP_PADDING = 100;
const CDN_URL = "https://cdn0.agoda.net/images/agoda-homes/partner-marketing/why-agoda-section";

const WhyAgodaImageMap: Record<string, string> = {
    card_1: "multilingual-support",
    card_2: "properties-worldwide",
    card_3: "data-rich-analytics",
    card_4: "manage-on-app",
};

const getScreenSize = (mediaQuery: MediaQuery) => {
    if (mediaQuery.xl || mediaQuery.l) return "desktop";
    if (mediaQuery.m) return "tablet";

    return "phone";
};

const getImageSource = (mediaQuery: MediaQuery, cardIndex: number) => {
    const screenSize = getScreenSize(mediaQuery);
    return `${CDN_URL}/${screenSize}/${WhyAgodaImageMap[`card_${cardIndex}`]}.svg`;
};

export const WhyAgodaSection: FunctionComponent = () => {
    const {
        whyAgodaSectionViewModel: { whyAgodaTitle, whyAgodaCards },
    } = usePartnerMarketingModelValue();
    const [isAtTop, setIsAtTop] = useState(false);
    const [scrollLocations, setScrollLocations] = useState<Record<string, number>>({});

    useEffect(() => {
        if (window.scrollY > 0) {
            window.scrollTo({ top: 0 });
            setIsAtTop(true);
        } else {
            setScrollLocations({
                card_1: document.getElementById("card_1")?.getBoundingClientRect().top,
                card_2: document.getElementById("card_2")?.getBoundingClientRect().top,
                card_3: document.getElementById("card_3")?.getBoundingClientRect().top,
                card_4: document.getElementById("card_4")?.getBoundingClientRect().top,
            });
        }
    }, []);

    useEffect(() => {
        if (isAtTop) {
            setScrollLocations({
                card_1: document.getElementById("card_1")?.getBoundingClientRect().top,
                card_2: document.getElementById("card_2")?.getBoundingClientRect().top,
                card_3: document.getElementById("card_3")?.getBoundingClientRect().top,
                card_4: document.getElementById("card_4")?.getBoundingClientRect().top,
            });
        }
    }, [isAtTop]);

    const mediaQuery = useMediaQueryContext();
    const CARD_TOP_OFFSET = mediaQuery.m ? 24 : 32;

    const CARD_BOUNDARIES: Record<string, Record<"start" | "end", number>> = {
        card_1: {
            start: 0,
            end: CARD_TOP_OFFSET - 1,
        },
        card_2: {
            start: CARD_TOP_OFFSET,
            end: 2 * CARD_TOP_OFFSET - 1,
        },
        card_3: {
            start: 2 * CARD_TOP_OFFSET,
            end: 3 * CARD_TOP_OFFSET - 1,
        },
    };

    function isInBoundaries(cardId: string, mouseYPos: number) {
        return mouseYPos >= CARD_BOUNDARIES[cardId].start && mouseYPos <= CARD_BOUNDARIES[cardId].end;
    }

    const handleCardClick = (currentIndex: number, e: MouseEvent) => {
        const currentCard = document.getElementById(`card_${currentIndex}`);
        if (e.target === e.currentTarget) {
            // click on the card
            if (currentCard.getBoundingClientRect().top > CARD_TOP_PADDING + 1) {
                // current card has not reached the top
                window.scrollTo({
                    top: scrollLocations[`card_${currentIndex - 1}`] - CARD_TOP_PADDING,
                    behavior: "smooth",
                });
            } else {
                // current card has reached the top
                // clicked on the padding of the next card
                const mouseYPos = e.clientY - currentCard.getBoundingClientRect().top;
                if (isInBoundaries("card_1", mouseYPos)) {
                    window.scrollTo({
                        top: scrollLocations.card_1 - CARD_TOP_PADDING,
                        behavior: "smooth",
                    });
                } else if (isInBoundaries("card_2", mouseYPos)) {
                    window.scrollTo({
                        top: scrollLocations.card_2 - CARD_TOP_PADDING,
                        behavior: "smooth",
                    });
                } else if (isInBoundaries("card_3", mouseYPos)) {
                    window.scrollTo({
                        top: scrollLocations.card_3 - CARD_TOP_PADDING,
                        behavior: "smooth",
                    });
                }
            }
        }
        if (e.target !== e.currentTarget && currentCard.getBoundingClientRect().top >= CARD_TOP_PADDING) {
            window.scrollTo({
                top: scrollLocations[`card_${currentIndex}`] - CARD_TOP_PADDING,
                behavior: "smooth",
            });
        }
    };

    const handleMouseEnterStackedCard = (currentIndex: number, e: MouseEvent) => {
        const currentCard = document.getElementById(`card_${currentIndex}`);
        if (e.currentTarget === e.target && currentCard.getBoundingClientRect().top <= CARD_TOP_PADDING) {
            document.body.style.cursor = "pointer";
        } else if (e.currentTarget !== e.target) {
            document.body.style.cursor = "default";
        }
    };

    return (
        <PartnerMarketingLayout
            py={mediaQuery.m ? "64" : "48"}
            col={8}
            backgroundColor="base.background"
            data-element-name="why-agoda-section"
        >
            <Box display="flex" flexDirection="column" alignItems="center" gap="32">
                <Typography variant="heading-2xl">{whyAgodaTitle}</Typography>
                <Box pb="32" width="100%" mb="64">
                    {whyAgodaCards.map((card, index) => (
                        <div
                            style={{
                                top: CARD_TOP_PADDING,
                                paddingTop: index * CARD_TOP_OFFSET,
                                height: "416px",
                                position: "sticky",
                            }}
                            id={`card_${index + 1}`}
                            key={card.title}
                            onClick={(e) => {
                                handleCardClick(index + 1, e);
                            }}
                            onMouseMove={(e) => {
                                handleMouseEnterStackedCard(index + 1, e);
                            }}
                            onMouseLeave={() => {
                                document.body.style.cursor = "default";
                            }}
                            data-element-name="why-agoda-card"
                            data-why-agoda-card={index}
                        >
                            <Card width="100%" variant="filled" backgroundColor="base.surface">
                                <Box
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    gap="16"
                                    height="416px"
                                    flexDirection="column"
                                    px="16"
                                    borderWidth="1"
                                    borderStyle="solid"
                                    borderRadius="16"
                                    borderColor="base.subtle"
                                    py="32"
                                    qM={{
                                        flexDirection: "row",
                                        px: "64",
                                        py: "48",
                                    }}
                                >
                                    <Image.Decorative
                                        src={getImageSource(mediaQuery, index + 1)}
                                        loading="immediate"
                                        responsive="original"
                                        width="295px"
                                        height="180px"
                                        qM={{
                                            width: "268px",
                                            height: "320px",
                                        }}
                                        qL={{
                                            width: "396px",
                                            height: "320px",
                                        }}
                                    />
                                    <Box
                                        display="flex"
                                        flexDirection="column"
                                        flex={1}
                                        gap={mediaQuery.m ? "24" : "16"}
                                    >
                                        <Typography variant="heading-xl" textAlign={mediaQuery.m ? "start" : "center"}>
                                            {card.title}
                                        </Typography>
                                        <Typography
                                            variant="body-default"
                                            textColor="base.subtle"
                                            textAlign={mediaQuery.m ? "start" : "center"}
                                        >
                                            {card.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Card>
                        </div>
                    ))}
                </Box>
            </Box>
        </PartnerMarketingLayout>
    );
};
